// src/components/document/DocumentPipelineView.tsx
// Kanban-style pipeline view for formal document lifecycle.
// Shows documents grouped by approval pipeline stages (Draft → In Review → Pending Approval → Approved → Published).

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import {
  groupDocumentsByPipelineStage,
  getEntityId,
  getDocumentTitle,
  getDocumentOwner,
  getDocumentType,
  getStatusMapping,
  documentTypeMap,
  formatRelativeTime,
  formatDocumentNumber,
  getReviewPriority,
} from '@/features/document/logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import type { PipelineStageGroup } from '@/features/document/types';

// ---------------------------------------------------------------------------
// Stage column colors
// ---------------------------------------------------------------------------

const STAGE_COLORS: Record<string, string> = {
  draft: 'border-t-muted-foreground/40',
  in_review: 'border-t-yellow-500',
  pending_approval: 'border-t-orange-500',
  approved: 'border-t-emerald-500',
  published: 'border-t-primary',
};

const STAGE_BG: Record<string, string> = {
  draft: 'bg-muted/30',
  in_review: 'bg-yellow-500/5',
  pending_approval: 'bg-orange-500/5',
  approved: 'bg-emerald-500/5',
  published: 'bg-primary/5',
};

// ---------------------------------------------------------------------------
// Pipeline card
// ---------------------------------------------------------------------------

function PipelineCard({
  doc,
  onSelect,
  onOpen,
  locale,
  t,
}: {
  doc: any;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  locale: string;
  t: (k: string, opts?: any) => string;
}) {
  const id = getEntityId(doc);
  const title = getDocumentTitle(doc);
  const owner = getDocumentOwner(doc);
  const docType = getDocumentType(doc);
  const typeInfo = getStatusMapping(documentTypeMap, docType);
  const docNumber = formatDocumentNumber(doc);
  const priority = getReviewPriority(doc);

  const priorityColors: Record<string, string> = {
    high: 'border-l-destructive',
    medium: 'border-l-yellow-500',
    low: 'border-l-transparent',
  };

  return (
    <div
      onClick={() => onSelect(id)}
      onDoubleClick={() => onOpen(id)}
      className={`rounded-lg border border-border bg-card p-3 cursor-pointer hover:shadow-sm transition-shadow border-l-2 ${priorityColors[priority]}`}
    >
      {/* Document number */}
      {docNumber && (
        <span className="text-[10px] font-mono text-muted-foreground block mb-1">
          {docNumber}
        </span>
      )}

      {/* Title */}
      <p className="text-sm font-medium text-foreground line-clamp-2 mb-2">
        {title || t('common.untitled')}
      </p>

      {/* Type badge */}
      <div className="flex items-center justify-between">
        <Badge variant={typeInfo.variant as any} size="sm">
          {t(typeInfo.labelKey)}
        </Badge>

        <div className="flex items-center gap-2">
          {/* Updated time */}
          <span className="text-[10px] text-muted-foreground">
            {doc.updated_at ? formatRelativeTime(doc.updated_at, locale) : ''}
          </span>

          {/* Owner avatar */}
          {owner && (
            <EntityAvatar name={owner} image={doc.owner?.avatar} userId={doc.owner?.entity?.id} size="xs" className="h-5 w-5 text-[9px]" />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pipeline column
// ---------------------------------------------------------------------------

function PipelineColumn({
  group,
  onSelect,
  onOpen,
  locale,
  t,
}: {
  group: PipelineStageGroup;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  locale: string;
  t: (k: string, opts?: any) => string;
}) {
  return (
    <div className={`flex flex-col rounded-lg border border-border border-t-2 ${STAGE_COLORS[group.stageId] || ''} ${STAGE_BG[group.stageId] || ''} min-w-[240px] max-w-[320px] flex-1`}>
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{t(group.labelKey)}</h3>
        <Badge variant="secondary" size="sm">{group.count}</Badge>
      </div>

      {/* Cards */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-280px)]">
        {group.documents.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-xs text-muted-foreground">{t('document.pipeline.noItems')}</p>
          </div>
        ) : (
          group.documents.map((doc: any) => (
            <PipelineCard
              key={getEntityId(doc)}
              doc={doc}
              onSelect={onSelect}
              onOpen={onOpen}
              locale={locale}
              t={t}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DocumentPipelineViewProps {
  documents: any[];
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}

export function DocumentPipelineView({ documents, onSelect, onOpen }: DocumentPipelineViewProps) {
  const { t, locale } = useI18n();

  const groups = useMemo(
    () => groupDocumentsByPipelineStage(documents),
    [documents],
  );

  const totalInPipeline = useMemo(
    () => groups.reduce((sum, g) => sum + g.count, 0),
    [groups],
  );

  return (
    <div className="space-y-3">
      {/* Pipeline summary */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span>{t('document.pipeline.totalInPipeline', { count: totalInPipeline })}</span>
      </div>

      {/* Kanban columns */}
      <div className="flex gap-3 overflow-x-auto pb-2" role="region" aria-label={t('document.pipeline.ariaLabel')}>
        {groups.map((group) => (
          <PipelineColumn
            key={group.stageId}
            group={group}
            onSelect={onSelect}
            onOpen={onOpen}
            locale={locale}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}
